/**
 * one-stop/chrome — the page chrome around the block list.
 *
 * The prototype's header, lifted: the derived-tint band, a `radius-sm` brand mark
 * in the brand fill, the wordmark with its lighter product word beside it, and
 * the nav on the right — 56 px tall, 28 px gutters, wrapping to the prototype's
 * 11/16 px mobile bar at 640 px.
 *
 * WHAT IS *NOT* HERE, and deliberately:
 *  - the prototype's `Signed out / Signed in` and `Desktop / Mobile` toolbar. Auth
 *    state is `session.authenticated` off the response and viewport is CSS;
 *    either control would ship as a bug.
 *  - the prototype's phone frame. It is a preview device, not a layout.
 *  - a sign-in button in the bar. The identity-panel block owns sign-in, because
 *    the DOCUMENT decides whether the page offers it.
 *
 * The footer is not here either — it is the `help-contact` BLOCK, so a page
 * without one simply has no footer, which is what the document says.
 */
import type { JSX, ReactNode } from "react";
import type { PortalHeader, PortalNavItem } from "@v0y4ge/api-contracts";

/** Up to two initials from the document's own title. No tenant name is baked. */
function initialsOf(title: string | null): string {
  if (!title) return "";
  const words = title
    .split(/\s+/u)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

export function OneStopHeader(props: {
  header: PortalHeader;
  nav: readonly PortalNavItem[];
  /** The slug currently rendered, for `aria-current`. */
  slug: string | null;
  /**
   * Whether the bar draws the title as the brand name. Default true, which is
   * every surface's behaviour and `get-help-first`'s.
   *
   * FALSE IS THE `site-landing` PROMOTION, NOT A SECOND BRAND SETTING. The
   * contract promotes `header.title` to a headline band above the hero grid
   * "instead of sitting in the page chrome", and the bar's name IS that same
   * string — so a host that draws the band passes false and the string renders
   * once. The INITIALS MARK still reads `header.title` either way: it is the
   * tenant's mark, not the page's headline, and dropping it would leave the bar
   * with nothing in it.
   */
  showTitle?: boolean;
}): JSX.Element {
  const initials = initialsOf(props.header.title);
  return (
    <header className="lv-header">
      <div className="lv-header-inner">
        <div className="lv-brand">
          {initials ? <div className="lv-brand-mark">{initials}</div> : null}
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
            {props.header.title && props.showTitle !== false ? <span className="lv-brand-name">{props.header.title}</span> : null}
            {props.header.eyebrow ? <span className="lv-brand-sub">{props.header.eyebrow}</span> : null}
          </div>
        </div>
        {props.nav.length > 0 ? (
          <nav className="lv-nav">
            {/* nav is DERIVED server-side and rendered from response.nav — never a
                client-side list of pages. */}
            {props.nav.map((item) => (
              <a
                key={item.slug}
                href={`/${item.slug}`}
                aria-current={item.slug === props.slug ? "page" : undefined}
              >
                {item.label}
              </a>
            ))}
          </nav>
        ) : null}
      </div>
    </header>
  );
}

/** The tinted root every One-Stop surface mounts inside, on both hosts. */
export function OneStopRoot({ children }: { children: ReactNode }): JSX.Element {
  return <div className="lv-root">{children}</div>;
}
